import { PREFERENCES_FOR_HEADER } from "../utils/preferencesDelegation.js";

export const USER_UI_PREFERENCES_SYNC_INTERVAL_MS = 1000;
const subscriptions = new Set();
let active = null;
let writeRevision = 0;
let pendingWrites = 0;

function userIsEditing(binding) {
  const element = binding.targetWindow?.document?.activeElement;
  return binding.pointerDown || element?.isContentEditable || element?.tagName === "TEXTAREA"
    || (element?.tagName === "INPUT" && !["checkbox", "radio", "button", "submit", "range", "color", "file", "hidden"].includes(element.type));
}

function sectionValue(state, section) {
  if (!section.startsWith("columns:")) return state[section];
  const tableKey = section.slice(8);
  return state.columns?.[tableKey] ?? { table_key: tableKey, column_order: [], column_widths: {}, column_labels: {}, frozen_column_keys: [] };
}

function deliver(binding) {
  if (active !== binding || !binding.state || pendingWrites || binding.stateRevision !== writeRevision || userIsEditing(binding)) return;
  for (const entry of subscriptions) {
    const value = sectionValue(binding.state, entry.section);
    const signature = JSON.stringify(value);
    if (entry.binding === binding && entry.signature === signature) continue;
    try {
      // Un lecteur occupé garde sa saisie locale. La prochaine vérification retente la livraison.
      if (entry.listener(value) === false) continue;
      entry.binding = binding;
      entry.signature = signature;
    } catch { /* Un lecteur en échec ne bloque pas les autres préférences. */ }
  }
}

export function subscribeUserUiPreferences(section, listener) {
  const entry = { section, listener, binding: null, signature: undefined };
  subscriptions.add(entry);
  if (active) deliver(active);
  return () => subscriptions.delete(entry);
}

// Appelé par le client HTTP commun : une réponse commencée avant une écriture locale devient périmée.
export function beginUserUiPreferencesWrite() {
  writeRevision++;
  pendingWrites++;
  let finished = false;
  return () => {
    if (finished) return;
    finished = true;
    pendingWrites--;
    writeRevision++;
    active?.refresh();
  };
}

export function startUserUiPreferencesSync({
  userId, fetchImpl, targetWindow = typeof window === "undefined" ? undefined : window,
  intervalMs = USER_UI_PREFERENCES_SYNC_INTERVAL_MS,
  setTimeoutImpl = globalThis.setTimeout, clearTimeoutImpl = globalThis.clearTimeout,
}) {
  active?.stop();
  const binding = { userId, targetWindow, pointerDown: false, state: null, stateRevision: -1, etag: "", stopped: false, running: false, requested: false, timer: null, failures: 0 };
  const controller = new AbortController();
  const schedule = (delay) => {
    clearTimeoutImpl(binding.timer);
    if (!binding.stopped) binding.timer = setTimeoutImpl(() => { void tick(); }, delay);
  };
  async function tick() {
    if (binding.stopped || binding.running) return;
    if (pendingWrites || targetWindow?.document?.visibilityState === "hidden") { schedule(intervalMs); return; }
    binding.running = true;
    binding.requested = false;
    const revision = writeRevision;
    try {
      const response = await fetchImpl("/api/me/ui-preferences/sync", {
        headers: { [PREFERENCES_FOR_HEADER]: userId, ...(binding.etag ? { "If-None-Match": binding.etag } : {}) },
        signal: controller.signal,
      });
      if (binding.stopped || revision !== writeRevision || pendingWrites) return;
      if (response.status !== 304) {
        if (!response.ok) throw new Error("Synchronisation des préférences indisponible.");
        const state = await response.json();
        if (binding.stopped || revision !== writeRevision || pendingWrites) return;
        if (state?.user_id !== userId || !state.interface || !state.columns || !state.browser) {
          throw new Error("L'identité ou le contenu des préférences reçues est invalide.");
        }
        binding.state = state;
        binding.etag = response.headers.get("etag") || "";
      }
      binding.failures = 0;
      binding.stateRevision = revision;
      deliver(binding);
    } catch (error) {
      if (error?.name !== "AbortError") binding.failures++;
    } finally {
      binding.running = false;
      schedule(binding.requested ? 0 : Math.min(30000, intervalMs * 2 ** Math.min(binding.failures, 5)));
    }
  }
  binding.refresh = () => {
    binding.requested = true;
    if (!binding.running) schedule(0);
  };
  const focus = () => binding.refresh();
  const resume = () => { binding.pointerDown = false; binding.refresh(); };
  const pointerDown = () => { binding.pointerDown = true; };
  const pointerUp = () => { binding.pointerDown = false; binding.refresh(); };
  const stop = () => {
    if (binding.stopped) return;
    binding.stopped = true;
    controller.abort();
    clearTimeoutImpl(binding.timer);
    targetWindow?.removeEventListener?.("focus", resume);
    targetWindow?.document?.removeEventListener?.("visibilitychange", focus);
    targetWindow?.document?.removeEventListener?.("focusout", focus);
    targetWindow?.document?.removeEventListener?.("pointerdown", pointerDown, true);
    targetWindow?.document?.removeEventListener?.("pointerup", pointerUp, true);
    targetWindow?.document?.removeEventListener?.("pointercancel", pointerUp, true);
    if (active === binding) active = null;
  };
  binding.stop = stop;
  active = binding;
  targetWindow?.addEventListener?.("focus", resume);
  targetWindow?.document?.addEventListener?.("visibilitychange", focus);
  targetWindow?.document?.addEventListener?.("focusout", focus);
  targetWindow?.document?.addEventListener?.("pointerdown", pointerDown, true);
  targetWindow?.document?.addEventListener?.("pointerup", pointerUp, true);
  targetWindow?.document?.addEventListener?.("pointercancel", pointerUp, true);
  void tick();
  return stop;
}
